// STEP 4-B · 판매자 전체 상품 전용 페이지
// - 기존 endpoint 재사용: GET /sellers/{seller_id}/products
// - 판매자 스코프 엄격 유지 (다른 셀러 상품 혼입 금지)
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Radio, ShoppingBag } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;

const SellerPublicProducts = () => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [products, setProducts] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [sellerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setNotFound(false);
      try {
        const [s, prods, todayRes] = await Promise.all([
          api.get(`/sellers/${sellerId}`),
          api.get(`/sellers/${sellerId}/products`).catch(() => ({ data: [] })),
          api.get(`/public/sellers/${sellerId}/today-broadcast`).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        setSeller(s.data);
        // 판매자 스코프 강제 (혼입 방지)
        setProducts((prods.data || []).filter((p) => !p.seller_id || p.seller_id === sellerId));
        setToday(todayRes.data?.broadcast || null);
      } catch (e) { if (e?.response?.status === 404) setNotFound(true); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [sellerId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF3B30]"/></div>;
  if (!seller || notFound) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center" data-testid="seller-all-products-not-found">
        <h2 className="text-xl font-bold mb-2">판매자를 찾을 수 없습니다.</h2>
        <Link to="/" className="text-[#FF3B30]">홈으로</Link>
      </div>
    </div>
  );

  const isLiveActive = !!today?.schedule_id;

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]" data-testid="seller-all-products-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <button onClick={() => navigate(`/seller/${sellerId}`)}
          className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-[#FF3B30] mb-4"
          data-testid="back-to-seller-profile">
          <ChevronLeft size={14}/> {seller.name} 프로필로
        </button>

        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight inline-flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            🛍 전체 상품
            <span className="text-sm text-gray-400" data-testid="all-products-count">{products.length}개</span>
          </h1>
          {(() => {
            // "오늘의 라이브 상품 보기" · 클릭 시 기존 LIVE watch(stream_url) 재사용 · 실제 LIVE 상태만 활성.
            // 새 LIVE viewer / detection API 생성 없음 · public/sellers/{id}/today-broadcast 응답 그대로 사용.
            const statusStr = String(today?.status || '').toLowerCase();
            const isRealLive = ['live', '라이브'].includes(statusStr);
            const watchUrl = today?.stream_url || today?.external_url || null;
            const canWatch = isRealLive && !!watchUrl;
            if (canWatch) {
              return (
                <a href={watchUrl} target="_blank" rel="noreferrer"
                  className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-black hover:bg-[#D32F2F] transition-colors"
                  data-testid="goto-live-products"
                  aria-label="현재 LIVE 방송보기">
                  <Radio size={10} className="animate-pulse"/> 오늘의 라이브 상품 보기
                </a>
              );
            }
            if (today?.schedule_id) {
              return (
                <span
                  className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-400 text-[11px] font-black cursor-not-allowed select-none"
                  title="현재 LIVE 방송이 시작되면 시청 가능합니다."
                  data-testid="goto-live-products-disabled">
                  <Radio size={10}/> LIVE 방송 대기중
                </span>
              );
            }
            return null;
          })()}
        </div>

        {products.length === 0 ? (
          <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-10 text-center" data-testid="all-products-empty">
            <ShoppingBag className="mx-auto text-gray-400 mb-3" size={32}/>
            <p className="text-sm font-bold mb-1">아직 등록된 판매 상품이 없습니다.</p>
            <p className="text-xs text-gray-500">이 판매자의 새로운 상품 소식을 팔로우해보세요.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {products.map((p) => {
              const isLiveOnly = p.sale_mode === 'live_only';
              const canBuy = !isLiveOnly || isLiveActive;
              return (
                <Link to={`/product/${p.product_id}`} key={p.product_id}
                  className={`rounded-lg overflow-hidden border border-black/5 dark:border-white/10 bg-white dark:bg-[#141414] hover:-translate-y-0.5 hover:shadow transition-all ${!canBuy ? 'opacity-70' : ''}`}
                  data-testid={`all-product-card-${p.product_id}`}>
                  <div className="relative aspect-square bg-gray-100 overflow-hidden">
                    <SafeImg src={p.image} kind="product" alt={p.name} className="w-full h-full object-cover"/>
                    {isLiveOnly && (
                      <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black inline-flex items-center gap-0.5">
                        <Radio size={8}/> {canBuy ? 'LIVE 중 구매' : 'LIVE 중만'}
                      </span>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium line-clamp-2 min-h-[2rem]">{p.name}</p>
                    <div className="flex items-baseline gap-1 mt-1">
                      {p.discount_rate > 0 && <span className="text-[#FF3B30] text-[10px] font-black">-{p.discount_rate}%</span>}
                      <span className="text-xs font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{won(p.sale_price)}</span>
                    </div>
                    {isLiveOnly && !canBuy && <p className="text-[9px] text-gray-400 mt-0.5">방송 시작 시 구매 가능</p>}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerPublicProducts;
